"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import NumberCard from "@/components/Cards/NumberCard";

import PrivateNumbersFAQ from "@/components/Home/PrivateNumbersFAQ";
import Link from "next/link";
import { Hyphenated } from "@/utils/Hyphenated";
import { getCountryInfo } from "@/data/country-info";
import { useLiveNumbers } from "@/lib/useLiveNumbers";

interface CountryObj {
  id: number;
  country_name: string;
  country_alpha_code: string;
  country_flag: string;
  country_tel_code: number;
  isActive?: boolean;
}

interface PhoneNumber {
  id: string;
  number: number;
  status: string;
  week_price?: number;
  countries: {
    country_flag: string;
    country_name: string;
    country_tel_code: number;
  };
}

interface CountryDataEntry {
  country: CountryObj;
  freeNumbers: any[];
  privateNumbers: PhoneNumber[];
  generatedAt: string;
}

interface Props {
  countriesData: Record<string, CountryDataEntry>;
  countries: CountryObj[];
}

export default function RentPrivateNumberDynamicContent({
  countriesData,
  countries,
}: Props) {
  const [countryParam, setCountryParam] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryObj | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [freeCount, setFreeCount] = useState(0);
  const [countryInfo, setCountryInfo] = useState<any>(null);
  const originalTitleRef = useRef<string>("");

  // Read country param from URL
  const getCountryFromUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("country");
  }, []);

  // Sync from URL on mount, on popstate (back/forward), and on custom countrychange event
  useEffect(() => {
    originalTitleRef.current = document.title;

    const syncFromUrl = () => {
      setCountryParam(getCountryFromUrl());
    };

    const handleCountryChange = (e: Event) => {
      const slug = (e as CustomEvent).detail;
      setCountryParam(slug || null);
    };

    syncFromUrl();
    window.addEventListener("popstate", syncFromUrl);
    window.addEventListener("countrychange", handleCountryChange);
    return () => {
      window.removeEventListener("popstate", syncFromUrl);
      window.removeEventListener("countrychange", handleCountryChange);
    };
  }, [getCountryFromUrl]);

  // Load country data when countryParam changes
  useEffect(() => {
    if (!countryParam) {
      setSelectedCountry(null);
      setPhoneNumbers([]);
      setFreeCount(0);
      setCountryInfo(null);
      cleanupMeta();
      return;
    }

    const data = countriesData[countryParam];
    if (data) {
      setSelectedCountry(data.country);
      setPhoneNumbers(data.privateNumbers || []);
      setFreeCount((data.freeNumbers || []).length);
      setCountryInfo(getCountryInfo(data.country.country_alpha_code));
    }
  }, [countryParam, countriesData]);

  // Overlay the build-time list with the current inventory (fallback: static).
  const liveNumbers = useLiveNumbers(selectedCountry?.country_alpha_code);
  useEffect(() => {
    if (liveNumbers) {
      setPhoneNumbers(liveNumbers.privateNumbers as any);
    }
  }, [liveNumbers]);

  // Inject/cleanup meta when selectedCountry changes
  useEffect(() => {
    if (selectedCountry) {
      injectMeta(selectedCountry);
    }
  }, [selectedCountry]);

  // Inject noindex meta and canonical for ?country= URLs
  const injectMeta = useCallback((country: CountryObj) => {
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex, follow");

    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", "https://smss.biz/rent-private-number/");

    document.title = `Rent Phone Number For ${country.country_name} | SMSS.BIZ`;
  }, []);

  const cleanupMeta = useCallback(() => {
    const robotsMeta = document.querySelector('meta[name="robots"][content="noindex, follow"]');
    if (robotsMeta) robotsMeta.remove();

    if (originalTitleRef.current) {
      document.title = originalTitleRef.current;
    }
  }, []);

  if (!countryParam || !selectedCountry) {
    return null;
  }

  const countryName = selectedCountry.country_name;
  const countrySlug = Hyphenated(countryName);

  const filterAvailableNumbers = phoneNumbers.filter(
    (number) => number.status.toLowerCase() === "available"
  );

  return (
    <div className="py-4 lg:py-14 space-y-10">
      {/* Available Numbers Count */}
      <h2 className="pt-14 text-gray-600 font-medium text-lg md:text-xl">
        {filterAvailableNumbers.length > 0
          ? `${filterAvailableNumbers.length} ${countryName} Phone Numbers Available to Rent`
          : `Sorry! No Numbers Are Available for ${countryName}`}
      </h2>

      {/* Number Cards Grid */}
      <div className="gap-4 w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {phoneNumbers?.map((number) => (
          <NumberCard
            isPrivate={true}
            key={number.id}
            country={`${number.countries.country_flag} ${number.countries.country_name}`}
            status={number.status.toLowerCase()}
            number={`+${number.countries.country_tel_code}-${number.number}`}
            href={`/account/rent-number?pn=${number.id}`}
          />
        ))}
      </div>

      {/* Trust / Description Section */}
      <div className="pt-8 space-y-4">
        <h2 className="font-semibold text-2xl text-gray-800">
          Your Trusted Platform to Rent Virtual Numbers in {countryName}
        </h2>
        <p className="text-zinc-600 font-medium">
          Join thousands of customers who rely on SMSS.BIZ to rent a {countryName} phone number online with confidence. Our dedicated {countryName} phone numbers are non-VoIP, exclusively assigned to you, and never shared with other users. If you only need a quick, one-time code, try a <Link href={`/free-temporary-numbers/${countrySlug}`} className="text-blue-600 hover:underline">free temporary {countryName} number</Link> first. For everything else — lease a virtual phone number in {countryName} on an ongoing subscription. Stay private, stay secure, and receive SMS online from {countryName} anytime.
        </p>
        <p className="text-zinc-600 font-medium">
          A private {countryName} phone number is ideal for anyone who needs a secure, exclusive number without exposing their personal line:
        </p>
        <ul className="list-disc list-inside text-zinc-600 font-medium space-y-2">
          <li>Register on social networks, dating apps, and messaging platforms using a private number for dating in {countryName}</li>
          <li>Access crypto exchanges, trading platforms, and financial services</li>
          <li>Sign up for freelancing, delivery, ecommerce, or ride-sharing apps using a dedicated {countryName} number</li>
          <li>Use as a long term verification number in {countryName} for business or personal accounts</li>
          <li>Test software, web applications, and APIs with a dedicated SMS number in {countryName}</li>
          <li>Run multiple accounts with a {countryName} number for multiple accounts — each with exclusive use</li>
        </ul>
      </div>

      {/* Why Rent Section */}
      <div className="space-y-4">
        <h2 className="font-semibold text-2xl text-gray-800">
          Why Rent a {countryName} Phone Number with SMSS.BIZ
        </h2>
        <p className="text-zinc-600 font-medium">
          When you rent a {countryName} virtual number monthly or long-term, you get a dedicated phone number in {countryName} that belongs to you alone. Unlike <Link href={`/free-temporary-numbers/${countrySlug}`} className="text-blue-600 hover:underline">shared temporary numbers</Link>, your private {countryName} phone number is reserved exclusively for your use — meaning no one else can see your incoming SMS or calls.
        </p>
        <p className="text-zinc-600 font-medium">
          Here is what makes our {countryName} number rental service stand out:
        </p>
        <ul className="list-disc list-inside text-zinc-600 font-medium space-y-2">
          <li><strong>Exclusive use phone number in {countryName}:</strong> Your number is never shared or reassigned.</li>
          <li><strong>{countryName} virtual number with area code:</strong> Choose from available area codes to match your target region.</li>
          <li><strong>Custom {countryName} virtual number:</strong> Select the number that suits your brand or personal preference.</li>
          <li><strong>Rent {countryName} number and keep it permanently:</strong> Renew your subscription and hold your number for as long as you need.</li>
        </ul>
        <p className="text-zinc-600 font-medium">
          Whether you need a premium {countryName} phone number for a growing business or a private verification number in {countryName} for personal use, SMSS.BIZ offers the flexibility and security to meet your needs.
        </p>
      </div>

      {/* Who Is It For Section */}
      <div className="space-y-6">
        <h2 className="font-semibold text-2xl text-gray-800">
          Who Is a {countryName} Private Phone Number For?
        </h2>
        <p className="text-zinc-600 font-medium">
          Our {countryName} phone number for rent online serves a wide range of users:
        </p>

        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg text-gray-800">Freelancers and Remote Workers</h3>
            <p className="text-zinc-600 font-medium">
              Lease a virtual phone number in {countryName} to separate work from personal life. Use it for platform registrations and account verifications without handing out your real number. For quick verifications that don&apos;t need a dedicated line, our <Link href={`/free-temporary-numbers/${countrySlug}`} className="text-blue-600 hover:underline">free {countryName} temporary numbers</Link> are also available.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg text-gray-800">Individuals Valuing Privacy</h3>
            <p className="text-zinc-600 font-medium">
              From a private number for dating in {countryName} to a private SMS number in {countryName} for account signups, our numbers ensure that your personal information stays private. Get exclusive use of your number with no shared access — ever.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg text-gray-800">Developers and QA Teams</h3>
            <p className="text-zinc-600 font-medium">
              A private {countryName} number for business testing makes it easy to simulate real-world SMS and voice flows. Use it for API testing, 2FA automation, OTP validation, and mobile app QA — without exposing real user data.
            </p>
          </div>
        </div>
      </div>

      {/* Feature Cards Section */}
      <div className="gap-4 place-content-center place-items-center justify-center items-center w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <div className="border rounded-xl p-6 space-y-3 h-full">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path fill="#655CFE" fillRule="evenodd" d="M1.5 4.5a3 3 0 0 1 3-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 0 1-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 0 0 6.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 0 1 1.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 0 1-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5Z" clipRule="evenodd" />
          </svg>
          <h3 className="font-semibold text-gray-800">New {countryName} Numbers Added Regularly</h3>
          <p className="text-sm text-zinc-600">
            New private {countryName} phone numbers are added to our inventory regularly and are available to rent 24/7. Whether you need a paid temporary number in {countryName} for a short campaign or a long term phone number in {countryName} for ongoing use, fresh inventory is always available.
          </p>
        </div>

        <div className="border rounded-xl p-6 space-y-3 h-full">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path fill="#00D3E0" fillRule="evenodd" d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
          </svg>
          <h3 className="font-semibold text-gray-800">Anonymous, Secure, and Exclusively Yours</h3>
          <p className="text-sm text-zinc-600">
            For rented private numbers, your identity and usage data are fully protected. Your private {countryName} phone number subscription is managed securely, and all data is permanently deleted when your rental ends.
          </p>
        </div>

        <div className="border rounded-xl p-6 space-y-3 h-full">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#AD00EA" className="w-8 h-8">
            <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 0 0-1.032-.211 50.89 50.89 0 0 0-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 0 0 2.433 3.984L7.28 21.53A.75.75 0 0 1 6 21v-4.03a48.527 48.527 0 0 1-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979Z" />
            <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 0 0 1.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0 0 15.75 7.5Z" />
          </svg>
          <h3 className="font-semibold text-gray-800">Receive SMS Online with Your {countryName} Number</h3>
          <p className="text-sm text-zinc-600">
            Your rented {countryName} number is ready to receive SMS online instantly. Use it as a private SMS reception number in {countryName} for any platform — social media, financial services, ecommerce, or business verification. You can also <Link href={`/free-temporary-numbers/${countrySlug}`} className="text-blue-600 hover:underline">receive SMS free with a shared {countryName} number</Link> for basic needs.
          </p>
        </div>

        <div className="border rounded-xl p-6 space-y-3 h-full">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#00CD5E" className="w-8 h-8">
            <path d="M8.25 4.5a3.75 3.75 0 1 1 7.5 0v8.25a3.75 3.75 0 1 1-7.5 0V4.5Z" />
            <path d="M6 10.5a.75.75 0 0 1 .75.75v1.5a5.25 5.25 0 1 0 10.5 0v-1.5a.75.75 0 0 1 1.5 0v1.5a6.751 6.751 0 0 1-6 6.709v2.291h3a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h3v-2.291a6.751 6.751 0 0 1-6-6.709v-1.5A.75.75 0 0 1 6 10.5Z" />
          </svg>
          <h3 className="font-semibold text-gray-800">Rent {countryName} Number with Voice 2FA</h3>
          <p className="text-sm text-zinc-600">
            Look for the 2FA voice icon next to available numbers. Many of our {countryName} virtual numbers support voice-based two-factor authentication, giving you a fully functional {countryName} number with voice capabilities.
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <PrivateNumbersFAQ
        countryName={countryName}
        countryParam={countrySlug}
        freeCount={freeCount}
        privateCount={phoneNumbers.length}
        dialCode={selectedCountry?.country_tel_code?.toString()}
        capital={countryInfo?.capital}
        majorCities={countryInfo?.majorCities}
      />

      {/* HowTo Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": `How to Rent a Private Phone Number in ${countryName}`,
            "description": `Rent a dedicated private ${countryName} phone number for SMS verification, business use, or personal privacy in under 2 minutes. Exclusive use, never shared.`,
            "totalTime": "PT2M",
            "url": `https://smss.biz/rent-private-number/?country=${countrySlug}`,
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": `Browse available private ${countryName} numbers`,
                "text": `Visit the SMSS.BIZ ${countryName} rental page and browse the available private phone numbers. Look for the voice 2FA icon if you need voice call support.`
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": `Select your dedicated ${countryName} number`,
                "text": `Choose a private ${countryName} phone number from the available inventory that suits your needs, including area code and voice capabilities.`
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Complete your rental subscription",
                "text": `Complete your rental subscription to exclusively reserve the ${countryName} phone number for your use. The number is assigned to you alone and never shared.`
              },
              {
                "@type": "HowToStep",
                "position": 4,
                "name": `Start receiving SMS and calls on your ${countryName} number`,
                "text": `Your dedicated ${countryName} number is immediately active. Use it to receive SMS verification codes and voice calls through your SMSS.BIZ dashboard in real-time.`
              }
            ]
          })
        }}
      />
    </div>
  );
}
